// @flow
import appsUtil from '../utils/AppsUtil';
import CompatibilityUtil from '../utils/CompatibilityUtil';
import {App, AppSectionBasedField} from '@ccxi/ccxi-apps/src/stores/app';
import AllAppTiles from '@ccxi/ccxi-apps/src/stores/apps';
import ContentParser from '@ccxi/ccxi-apps/src/utils/contentParser';
import SkeletonModelMapper from '@ccxi/ccxi-apps/src/utils/modelMapper/skeletonModelMapper';
import AppHandlers from '../containers/AppHandlers/AppHandlers';
import AppsAnalytics from '../analytics/AppsAnalytics';
import {ENTITLEMENTS} from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/appJsonConstants';
import {
	AppsPanelSkeleton, SectionGroup,
} from '@ccxi/ccxi-apps/src/stores/skeleton';
import CTAUtil from '../utils/CTAUtil';
import {isTestAExperience} from '../utils/TestUtil';
import thorUtil from '../utils/wam/ThorUtil';
import thorCommonUtil from '../utils/wam/ThorCommonUtil';
import PDPModelMapper from './PDPModelMapper';
import AppInformationModelMapper from './AppInformationModelMapper';
import {appButtonConfig, subHeadingFilter, FFCLocaleMap, campaignId} from '../constants/appsConstants';
import AppInformationDialog from '@ccxi/ccxi-apps/src/stores/dialog/appInformationDialog';
import AllAppsBannerModelMapper from '@ccxi/ccxi-apps/src/utils/modelMapper/banner/allAppsBannerModelMapper';
import {APP_PLATFORM} from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/appModelConstants';
import AppModelMapper from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper';
import CategoriesBannerModelMapper from '@ccxi/ccxi-apps/src/utils/modelMapper/banner/categoriesBannerModelMapper';
import {PDP_SECTION, PDP_ACTION} from '@ccxi/ccxi-apps/src/utils/pdpUtils/pdpEnums';
import {INFORMATION_DIALOG_ID, LEFT_NAV_KEYS, SKELETON_CTA_KEYS} from '@ccxi/ccxi-apps/src/utils/enums';

class ModelParserUtil {
	createApplineage(productConfig: Object, platformType: string) {
		if (productConfig.appLineage) {
			appsUtil.appLineageMap[productConfig.appLineage] = appsUtil.appLineageMap[productConfig.appLineage] || {platformTypeList:[], platformAppList:[]};
			(appsUtil.appLineageMap[productConfig.appLineage].platformTypeList.indexOf(platformType) === -1) && appsUtil.appLineageMap[productConfig.appLineage].platformTypeList.push(platformType);
			(appsUtil.appLineageMap[productConfig.appLineage].platformAppList.indexOf(productConfig.id) === -1) && appsUtil.appLineageMap[productConfig.appLineage].platformAppList.push(productConfig.id);
		}
	}

	fillAppsModel(productArr: Object[], platformType: string, hasThor: boolean, inputLocale: string) {
		const productsList = {};
		const entitledProducts = [];
		for (let index = 0; index < productArr.length; index++) {
			const sapcode = productArr[index].id;
			const isVersionEnabled = appsUtil.isVersionEnabled(productArr[index]);
			if (isVersionEnabled
				&& !appsUtil.disableProduct(sapcode)
				&& (appButtonConfig[sapcode] || (platformType !== APP_PLATFORM.DESKTOP && appsUtil.getProductIdFromSapcode(sapcode)))
				&& (!appsUtil.hasFeature('temp_hide_muse_for_unentitled') || sapcode !== 'MUSE' || this.getEntitlement(productArr[index]))) {
				productsList[productArr[index].id] = productArr[index];
				if (this.createAppsModel(productArr[index],platformType, hasThor, inputLocale)) {
					entitledProducts.push(productArr[index].id);
				}
			}
		}

		// update subSectionFilter from lineage

		return {entitledProducts,productsList};
	}

	createAppsModel(productConfig: Object, platformType: string, hasThor: boolean, inputLocale: string) {
		const invertKcccTileOrder = productConfig.id === 'KCCC' && hasThor;
		const sortOrder = {all: invertKcccTileOrder ? Number.MAX_SAFE_INTEGER : productConfig.sortIndex};
		let icons;
		if (platformType === APP_PLATFORM.WEB) {
			icons = productConfig.serviceIcons.icon;
		}
		else if (platformType === APP_PLATFORM.MOBILE) {
			icons = productConfig.mobileAppIcons.icon;
		}
		else {
			icons = productConfig.productIcons.icon;
		}
		const iconObj = icons && icons.find(elm => elm.size === '96x96');
		const entitled = this.getEntitlement(productConfig);
		const isFreemium = this.getIsFreemium(productConfig);
		const sapCode = productConfig.id;
		const CTAUtilObj = new CTAUtil({
			type: platformType,
			sapCode,
			entitled,
			displayName: productConfig.displayName,
			hasThor
		});

		let appSectionFilter = productConfig.categories.category ? productConfig.categories.category : [];
		appSectionFilter = appSectionFilter.map((obj) => { sortOrder[obj.value] = invertKcccTileOrder ? Number.MAX_SAFE_INTEGER : (obj['sort-order'] || 10000); return obj.value;}).concat(['all', 'pdp']);

		appsUtil.CCXUIAppStore[`${productConfig.id}_${productConfig.version}`] = new App(
			{
				sapcode: productConfig.id,
				codexVersion: productConfig.version,
				platform: platformType,
				iconURL: iconObj && iconObj.value,
				appFamilyName: productConfig.displayName,
				onAppAction: AppHandlers.handleAppAction.bind(AppHandlers),
				actionIds: {
					select: 'appTileSelect'
				},
				sortOrder,
				supportedOS: this.getSupportedOSList(productConfig),
				entitlementInfo: {
					status: entitled ? ENTITLEMENTS.LICENSE_ACTIVE : ENTITLEMENTS.TRIAL_AVAILABLE
				},
				progress: null,
				appLineage: productConfig.appLineage,
				appSectionFilter,
				showCompatibility: platformType === APP_PLATFORM.DESKTOP,
				subSectionFilter: [platformType], // , // how to check if an app is available for other platforms?
				subHeadingFilter: this.getSubHeadingFilter(entitled, platformType, isFreemium),
				actionList: CTAUtilObj.getButtonList(false,true).concat(thorCommonUtil.getWAMCTAs()),
				productId: appsUtil.getProductIdFromSapcode(productConfig.id),
				appSectionBasedFields: this.getAppSectionBasedFields(productConfig,platformType,inputLocale,entitled,CTAUtilObj),
				isPDPDataAvailable: true
			}
		);

		if (appsUtil.isCCHInLMS()) {
			if (entitled) {
				AllAppTiles.addApp(appsUtil.CCXUIAppStore[`${productConfig.id}_${productConfig.version}`]);
			}
		}
		else {
			AllAppTiles.addApp(appsUtil.CCXUIAppStore[`${productConfig.id}_${productConfig.version}`]);
		}

		return entitled;
	}

	getAppSectionBasedFields(productConfig: Object, platformType: string, inputLocale: ?string, entitled: boolean, CTAUtilObject?: Object) {
		const CTAUtilObj = CTAUtilObject ? CTAUtilObject : new CTAUtil({
			type: platformType,
			sapCode: productConfig.id,
			entitled,
			displayName: productConfig.displayName,
			hasThor: appsUtil.hasThor
		});
		let appSectionFilter = productConfig.categories.category ? productConfig.categories.category : [];
		appSectionFilter = appSectionFilter.map(obj => obj.value);
		let buttonList = CTAUtilObj.getButtonList();
		let dropDownList = CTAUtilObj.getDropDownList();
		let categoryDropDownList = [];
		// If button list overflows, move secondary buttons to dropdown list
		const shouldMove = AppModelMapper.shouldMoveSecondaryButtonToDropdownList(buttonList);
		if (shouldMove) {
			const filteredButtonList = buttonList.filter(button => (button.actionType.variant !== 'secondary'));
			const actionsToPushInDropdown = buttonList.filter(button => (button.actionType.variant === 'secondary'));
			categoryDropDownList = actionsToPushInDropdown;
			dropDownList = actionsToPushInDropdown.concat(dropDownList);
			buttonList = filteredButtonList;
		}
		const appSectionBasedFields = [
			new AppSectionBasedField({
				availablePlatforms: productConfig.appLineage ? appsUtil.appLineageMap[productConfig.appLineage].platformTypeList : [platformType],
				appSectionFilter: ['all', 'pdp'],
				subTextType: 'info',
				localizedTagline: appsUtil.getProductTagLine(productConfig, inputLocale),
				buttonList,
				dropDownList,
				showStatusLabel: true
			})
		];
		if (appSectionFilter && appSectionFilter.length) {
			appSectionBasedFields.push(new AppSectionBasedField({
				availablePlatforms: productConfig.appLineage ? appsUtil.appLineageMap[productConfig.appLineage].platformTypeList : [platformType],
				appSectionFilter,
				subTextType: 'info',
				localizedTagline: appsUtil.getProductTagLine(productConfig, inputLocale),
				buttonList,
				dropDownList: categoryDropDownList,
				showSessionErrorOnly: true,
				showStatusLabel: true,
				isCardClickable: true,
				isCategorySection: true
			}));
		}
		return appSectionBasedFields;
	}

	getSubHeadingFilter(entitled: boolean, platformType: string, isFreemium: boolean) {
		const subHeading = [platformType];
		if (!entitled) {
			subHeading.push(subHeadingFilter.trailApps);
		}
		else if (appsUtil.hasFeature('temp_contentful_to_aem_migration') && appsUtil.hasFeature('temp_freemium_apps_aem') && isFreemium) {
			subHeading.push(subHeadingFilter.freemium);
		}
		else {
			subHeading.push(subHeadingFilter.subscribed);
		}
		return subHeading;
	}

	updateSpecificAppFields(productConfig: Object, entitled: boolean, appPlatform: string, isFreemium: boolean) {
		appsUtil.CCXUIAppStore[`${productConfig.id}_${productConfig.version}`].setAppSectionBasedFields(this.getAppSectionBasedFields(productConfig,appPlatform,appsUtil.locale,entitled));
		appsUtil.CCXUIAppStore[`${productConfig.id}_${productConfig.version}`].setSubHeadingFilter(this.getSubHeadingFilter(entitled, appPlatform, isFreemium));
	}

	updateAppModelFields() {
		let productConfig = {};
		/* Loop through desktop products*/
		if (appsUtil.appsFFCProductList && appsUtil.isUserEnterpriseUser()) {
			Object.keys(appsUtil.appsFFCProductList).forEach((key) => {
				productConfig = appsUtil.appsFFCProductList[key];
				const entitled = this.getEntitlement(productConfig);
				const isFreemium = this.getIsFreemium(productConfig);
				if (!entitled) {
					this.updateSpecificAppFields(productConfig, entitled, APP_PLATFORM.DESKTOP, isFreemium);
				}
			});
		}

		/* Loop through web app services*/
		if (appsUtil.appsFFCServiceList) {
			Object.keys(appsUtil.appsFFCServiceList).forEach((key) => {
				productConfig = appsUtil.appsFFCServiceList[key];
				const entitled = this.getEntitlement(productConfig);
				const isFreemium = this.getIsFreemium(productConfig);
				this.updateSpecificAppFields(productConfig, entitled, APP_PLATFORM.WEB, isFreemium);
			});
		}
		/* Loop through mobile apps */
		if (appsUtil.appsFFCMobileList) {
			Object.keys(appsUtil.appsFFCMobileList).forEach((key) => {
				productConfig = appsUtil.appsFFCMobileList[key];
				const entitled = this.getEntitlement(productConfig);
				const isFreemium = this.getIsFreemium(productConfig);
				this.updateSpecificAppFields(productConfig, entitled, APP_PLATFORM.MOBILE, isFreemium);
			});
		}
	}

	updateAppModelFieldsPostThorCheck() {
		let productConfig = {};
		/* Loop through desktop products*/
		if (appsUtil.appsFFCProductList) {
			Object.keys(appsUtil.appsFFCProductList).forEach((key) => {
				productConfig = appsUtil.appsFFCProductList[key];
				const entitled = this.getEntitlement(productConfig);
				const isFreemium = this.getIsFreemium(productConfig);
				this.updateSpecificAppFields(productConfig, entitled, APP_PLATFORM.DESKTOP, isFreemium);
			});
		}
	}

	getSupportedOSList(productConfig: Object) {
		const supportedOS = [];
		if (productConfig.supportedOSList && productConfig.supportedOSList.supportedOS) {
			productConfig.supportedOSList.supportedOS.forEach((obj) => {
				const osName = obj.value.toLowerCase() === 'ios' ? 'Apple' : obj.value;
				supportedOS.push(osName);
			});
		}
		return supportedOS;
	}

	getEntitlement(productConfig: Object) {
		let entitled = true;
		if (appsUtil.isAppEntitledNew(productConfig, productConfig.id, productConfig.type) && !appsUtil.disableProduct(productConfig.id)) {
			entitled = true;
		}
		else {
			entitled = false;
		}
		return entitled;
	}

	getIsFreemium(productConfig: Object) {
		return appsUtil.isAppFreemium(productConfig, productConfig.id, productConfig.type);
	}

	updateAppSectionForMobile(appSection: Object) {
		if (appSection.sectionHeaderList) {
			const sectionHeaderList = appSection.sectionHeaderList.reduce((updatedList, header) => {
				if (header.id === 'mobile') {
					updatedList.unshift(header);
				}
				else {
					updatedList.push(header);
				}
				return updatedList;
			}, []);
			appSection.sectionHeaderList = sectionHeaderList;
		}
		if (appSection.subSectionList) {
			appSection.subSectionList.forEach((subSection) => {
				if (subSection.id === 'mobile') {
					subSection.isDefault = true;
				}
			});
		}
		return appSection;
	}

	fillAppSkeletonModel(skeletonJson: ?Object) {
		if (!skeletonJson) {
			return;
		}
		let parsedSkeletonData;
		let items = {};
		if (appsUtil.hasFeature('temp_contentful_to_aem_migration')) {
			parsedSkeletonData = skeletonJson.data.experienceAppsSkeletonByPath;
			if (parsedSkeletonData.item) {
				items = parsedSkeletonData.item.sectionGroupList;
			}
		}
		else {
			parsedSkeletonData = ContentParser.parse(skeletonJson);
			if (parsedSkeletonData.items) {
				items = parsedSkeletonData.items[0].sectionGroupList;
			}
		}

		AppsPanelSkeleton.clearSectionGroup();
		Object.keys(items).forEach((key) => {
			const appSections = [];
			if (items[key].inAppID === 'categories') {
				Object.keys(items[key].sectionList).forEach((key1) => {
					const sectionLabel = appsUtil.intl.formatMessage({id: 'apps.category.cc.heading'},{categoryLabel: items[key].sectionList[key1].text});
					const newSection = {
						id: 'desktop',
						includedSapCodeList: ['KCCC'],
						layoutId: 'appSectionListView',
						minimumAppCount: 1,
						text:  sectionLabel
					};
					if (appsUtil.hasThor) {
						items[key].sectionList[key1].headerList = [...(items[key].sectionList[key1].headerList), newSection];
					}
					else {
						items[key].sectionList[key1].headerList = [newSection, ...(items[key].sectionList[key1].headerList)];
					}
				});
			}
			Object.keys(items[key].sectionList).forEach((key1) => {
				if (appsUtil.isMobileOrTab()
					&& items[key].sectionList[key1].inAppID === 'updates') {
					return;
				}

				const data = items[key].sectionList[key1];
				// Remove menuList for updates Section.
				if (data.inAppID === LEFT_NAV_KEYS.UPDATES_KEY) {
					data.contextMenuList = [];
				}
				let appSection = SkeletonModelMapper.toAppSection(data,AppHandlers.handleSkeletonItemClick.bind(AppHandlers), AppHandlers.handleSkeletonItemClick.bind(AppHandlers));
				// TODO: check updates section on mobile
				if (appsUtil.isMobileOrTab()) {
					appSection = this.updateAppSectionForMobile(appSection);
				}
				appSections.push(appSection);
			});

			AppsPanelSkeleton.addSectionGroup(new SectionGroup(
				items[key].text,
				appSections,
				items[key].inAppID
			));
		});
		// Handling update case
		AppsPanelSkeleton.disableActionItem(LEFT_NAV_KEYS.APPS_SECTION_GROUP_KEY, LEFT_NAV_KEYS.UPDATES_KEY, SKELETON_CTA_KEYS.UPDATE_ALL_KEY);
	}

	handlerBannerClick(urlObj: Object, guid: string) {
		if (urlObj.targetType === 'send-link') {
			const getParams = (url) => {
				const params = {};
				const query = url.substring(url.indexOf('//') + 2);
				const vars = query.split('&');
				for (let index = 0; index < vars.length; index++) {
					const pair = vars[index].split('=');
					params[pair[0]] = decodeURIComponent(pair[1]);
				}
				return params;
			};
			const urlParams = getParams(urlObj.url);
			const sapcode = urlParams.sapCode;
			const source = 'banner';
			AppHandlers.openSendMeLinkDialog(
				'send-link',
				appsUtil.CCXUIAppStore[`${sapcode}_${appsUtil.getFFCProductConfigBySapCode(sapcode).version}`],
				{source}
			);
			return;
		}
		AppHandlers.openExternalLink(urlObj.url, undefined, guid, 'banner');
	}

	fillAllAppBannerModel(appsBannerJson: ?Object, subSectionId: string) {
		AllAppsBannerModelMapper.setAllAppsBanners(appsBannerJson, (urlObj, guid) => {
			this.handlerBannerClick(urlObj, guid);
		}, undefined, undefined, undefined, undefined, subSectionId);
		if (appsBannerJson === null && !isTestAExperience(campaignId.bannerOnMobile)) {
			const appAnalytics = new AppsAnalytics(window.ENV);
			appAnalytics.initAnalytics();
			appAnalytics.onAppsErrorEvent(`aem-banner-${subSectionId}`);
		}
	}

	fillCategoriesBannerModel(categoriesBannerJson: ?Object) {
		appsUtil.banner = appsUtil.banner || {};
		if (!appsUtil.banner.isCategoreisBannnerAdded) {
			appsUtil.banner.isCategoreisBannnerAdded = true;
			CategoriesBannerModelMapper.setCategoriesBanners(categoriesBannerJson, (urlObj, guid) => {
				this.handlerBannerClick(urlObj, guid);
			});
		}
	}

	fillPDPModel(PDPJson: Object, hasThor: boolean, inputLocale: string, resolve: Function, reject: Function) {
		if (PDPJson) {
			const parsedPDPData = ContentParser.parse(PDPJson.data);
			let PDPItems = {};
			if (parsedPDPData.items && parsedPDPData.items.length > 0) {
				PDPItems = parsedPDPData.items[0];
			}
			else {
				PDPItems = PDPJson.data.data.appsPdpByPath.item;
			}

			if (typeof PDPItems === 'undefined') {
				reject(false);
				return false;
			}
			if (PDPItems.sapCode) {
				PDPItems.sapCode = PDPItems.sapCode.trim();
			}
			// Update LanguageList
			this.updateLanguageListFromSupportedLanguages(PDPItems);
			// fill button data
			const productConfig = appsUtil.getFFCProductConfigBySapCode(PDPItems.sapCode);
			const controllerData = {};
			appsUtil.CCXUIAppStore[`${PDPItems.sapCode}_${productConfig.version}`].isIncompatible = !(CompatibilityUtil.isAppSupported(PDPItems.sapCode) || CompatibilityUtil.isAnyOlderVersionSupported(PDPItems.sapCode));
			controllerData.appObject = appsUtil.CCXUIAppStore[`${PDPItems.sapCode}_${productConfig.version}`];
			const entitled = this.getEntitlement(productConfig);
			const sapCode = productConfig.id;
			const CTAUtilObj = new CTAUtil({
				type: controllerData.appObject.platform,
				sapCode,
				entitled,
				displayName: productConfig.displayName,
				hasThor
			});
			if (controllerData.appObject && controllerData.appObject.platform === APP_PLATFORM.DESKTOP && thorUtil.shouldUpdatePDP(PDPItems.sapCode, productConfig.version)) {
				controllerData.applicableActionList = thorUtil.getPDPActionList(PDPItems.sapCode, productConfig.version);
			}
			else {
				controllerData.applicableActionList = CTAUtilObj.getButtonList(true);
			}
			const AppInformationDialog = this.toAppInformationDialog(PDPItems,productConfig);
			appsUtil.informationDialogStore[PDPItems.sapCode] = AppInformationDialog;

			// call PDPModelMapper with PDPItems ,appObject,AppHandlers.handlePDPAction.bind(AppHandlers)
			appsUtil.CCXUIPDPStore[PDPItems.sapCode] = PDPModelMapper.toPDPModel(PDPItems, controllerData, AppHandlers.handlePDPAction.bind(AppHandlers));
			resolve(appsUtil.CCXUIPDPStore[PDPItems.sapCode]);
		}
		else {
			reject(PDPJson);
		}
	}

	updateLanguageListFromSupportedLanguages(PDPItems: Object) {
		if (PDPItems.supportedLanguages) {
			PDPItems.languageList = [];
			PDPItems.supportedLanguages.forEach((value) => {
				if (appsUtil.locale && value.languageId === FFCLocaleMap[appsUtil.locale]) {
					PDPItems.languageList.unshift(value.languageName);
				}
				else {
					PDPItems.languageList.push(value.languageName);
				}
			});
		}
	}

	getCategories(app: Object) {
		// fill from ffc
		const categories = {
			sectionId: PDP_SECTION.CATEGORIES,
			categoryList: []
		};
		let appSectionFilter = app.categories.category ? app.categories.category : [];
		appSectionFilter = appSectionFilter.map(obj => obj.value);
		appSectionFilter.forEach((value) => {
			categories.categoryList.push({
				actionId: PDP_ACTION.CATEGORY_LABEL_CLICK,
				id: value,
				text: value
			});
		});

		return categories;
	}

	toAppInformationDialog(PDPItems: Object, productConfig: Object) {
		const AppInformationDialogSections: Object = AppInformationModelMapper.toAppInformationDialogSections(PDPItems);
		AppInformationDialogSections.version = this.getVersionsModel();
		AppInformationDialogSections.actionList = this.getInformationDialogActionList();
		AppInformationDialogSections.onAction = AppHandlers.handleInformationDialogAction.bind(AppHandlers);
		AppInformationDialogSections.app = appsUtil.CCXUIAppStore[`${PDPItems.sapCode}_${productConfig.version}`];
		const appInformationDialogObj = new AppInformationDialog(AppInformationDialogSections);
		return appInformationDialogObj;
	}

	getInformationDialogActionList() {
		return [
			{
				actionId: INFORMATION_DIALOG_ID.DONE_CLICK
			}
		];
	}

	getVersionsModel() {
		return {
			//        actionId: 'modal-tab-select',
			//        tabId: 'version',
			//        tabTitle: 'Version',
			//        currentVersion: [], // List of app Objects.
			//        previousVersions: [], // List of app Objects.
			//        subText: {
			//            text: 'Looking for older version?',
			//            viewMore: {
			//                actionId: 'view-more-click',
			//                text: 'View More',
			//                url: 'esd.com',
			//            },
			//        },
		};
	}
}

export default new ModelParserUtil();
